import { Card } from "@dropbox/dash-component-library";
import { Text } from "@dropbox/dig-components/typography";
import { PAP_Delete_StackSection } from "@mirage/analytics/events/types/delete_stack_section";
import { PAP_Rename_StackSection } from "@mirage/analytics/events/types/rename_stack_section";
import { PAP_Select_StackSection } from "@mirage/analytics/events/types/select_stack_section";
import { useStackPageAugustRevisionEnabled } from "@mirage/august-revision-hook";
import { upsertStack } from "@mirage/service-stacks";
import {
  CardHeaderType,
  TwoColumnGridCard,
} from "@mirage/shared/two-column-grid/TwoColumnGridCard";
import { MAX_STACK_SECTION_COUNT } from "@mirage/shared/util/constants";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import {
  activeStackActionAtom,
  activeStackAtom,
  activeStackHasWritePermissionsAtom,
  activeStackMutationRequestIdAtom,
  activeStackSectionIdAtom,
  activeStackSectionsAtom,
} from "@mirage/stacks/ActiveStack/atoms";
import { SectionDropTargetWrapper } from "@mirage/stacks/FullScreenStack/DragAndDrop/SectionDropTargetWrapper";
import { useReportStackPapEvent } from "@mirage/stacks/hooks/useReportStackPapEvent";
import { getTheme } from "@mirage/stacks/themes";
import i18n from "@mirage/translations";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCreateStackSection } from "../hooks";
import { SectionsActionType } from "../types";
import { asStackUpsertId } from "../utils";
import { CreateSectionButton } from "./CreateSectionButton";
import { useSectionsDragDrop } from "./DragDrop";
import styles from "./Navigation.module.css";
import { NavigationItem } from "./NavigationItem";
import { InlineCreateSection, NavigationList } from "./NavigationList";

import type { stacks } from "@dropbox/api-v2-client";
import type { DragDropListItem } from "@mirage/drag-and-drop/DragDropList";

interface NavigationProps {
  scrollToSection: (sectionId: string) => void;
  publicPreview?: boolean;
  // this override flag is used to display the dfb august revision styles
  // when we want to render the new styles but can't feature gate users
  // ex. public view of the stacks page using (dfi styles, and we wanted to display dfb styles)
  alwaysUseAugustRevisionDfBStyles?: boolean;
}

export const Navigation: React.FC<NavigationProps> = ({
  scrollToSection,
  alwaysUseAugustRevisionDfBStyles,
}) => {
  const reportStackPapEvent = useReportStackPapEvent();
  const isAugustRevisionEnabled =
    useStackPageAugustRevisionEnabled() || alwaysUseAugustRevisionDfBStyles;

  const stack = useAtomValue(activeStackAtom);
  const [{ allSections: sections }, setSections] = useAtom(
    activeStackSectionsAtom,
  );
  const hasWritePermissions = useAtomValue(activeStackHasWritePermissionsAtom);
  const [currentAction, setCurrentAction] = useAtom(activeStackActionAtom);
  const currentSectionId = useAtomValue(activeStackSectionIdAtom);

  const onCreateSection = useCreateStackSection();
  const { otherSections, onDrop } = useSectionsDragDrop();
  const mutationId = useAtomValue(activeStackMutationRequestIdAtom);
  // There's a strange issue where the value of `currentSectionId` gets frozen
  // inside the drag-and-drop component. Therefore, use a ref to make sure the
  // value is always updated.
  const currentSectionIdRef = useRef<string | null>(null);
  const [, forceRefresh] = useState(false);

  useEffect(() => {
    currentSectionIdRef.current = currentSectionId;
    forceRefresh((v) => !v);
  }, [currentSectionId]);

  const [isEditing, setIsEditing] = useState(false);

  const handleScrollToSection = useCallback(
    (sectionId: string) => {
      scrollToSection(sectionId);
      reportStackPapEvent(PAP_Select_StackSection());
    },
    [reportStackPapEvent, scrollToSection],
  );

  const upsertStackFn = useCallback(
    async (newSections: stacks.Section[]) => {
      const update: stacks.StackDataUpdate = {
        field: {
          ".tag": "section_data_update",
          sections: newSections,
        },
      };
      await upsertStack(asStackUpsertId(stack?.namespace_id, mutationId), [
        update,
      ]);
      setSections(newSections);
    },
    [stack, mutationId, setSections],
  );

  const onRenameSection = useCallback(
    async (newName: string, sectionId: string) => {
      if (!sections) return;

      const newSections = sections.map((section) => {
        if (section.id === sectionId) {
          return {
            ...section,
            title: newName,
          };
        }
        return section;
      });
      reportStackPapEvent(PAP_Rename_StackSection());
      upsertStackFn(newSections);
    },
    [reportStackPapEvent, sections, upsertStackFn],
  );

  const onDeleteSection = useCallback(
    async (sectionId: string) => {
      if (!sections) return;

      const newSections = sections.filter(
        (section) => section.id !== sectionId,
      );
      reportStackPapEvent(
        PAP_Delete_StackSection({
          featureLine: "stacks",
        }),
      );
      upsertStackFn(newSections);
    },
    [reportStackPapEvent, sections, upsertStackFn],
  );

  const listItems: DragDropListItem[] = useMemo(() => {
    if (!otherSections) {
      return [];
    }

    return otherSections.map((section) => {
      const sectionId = section.id ?? "";
      return {
        id: `${sectionId}-${section.title}-${hasWritePermissions}`,
        key: sectionId,
        render: () => (
          <SectionDropTargetWrapper sectionId={sectionId}>
            <NavigationItem
              key={section.id}
              hasWritePermissions={hasWritePermissions ?? false}
              onDeleteSection={onDeleteSection}
              onRenameSection={onRenameSection}
              scrollToSection={handleScrollToSection}
              section={section}
            />
          </SectionDropTargetWrapper>
        ),
      };
    });
  }, [
    handleScrollToSection,
    hasWritePermissions,
    onDeleteSection,
    onRenameSection,
    otherSections,
  ]);

  useEffect(() => {
    if (currentAction !== SectionsActionType.CREATE_SECTION) {
      setIsEditing(false);
    }
  }, [currentAction]);

  const hasTooManySections = sections
    ? sections.length >= MAX_STACK_SECTION_COUNT
    : false;

  const createButtonDisabled = hasTooManySections || !hasWritePermissions;

  const createButton = !hasWritePermissions ? (
    <DigTooltip title={i18n.t("read_only_section_tooltip")}>
      <div style={{ maxWidth: "150px" }}>
        <CreateSectionButton
          disabled={createButtonDisabled}
          setIsEditing={setIsEditing}
          setCurrentAction={setCurrentAction}
          isAugustRevisionEnabled={
            isAugustRevisionEnabled || alwaysUseAugustRevisionDfBStyles
          }
        />
      </div>
    </DigTooltip>
  ) : (
    <CreateSectionButton
      disabled={createButtonDisabled}
      setIsEditing={setIsEditing}
      setCurrentAction={setCurrentAction}
      isAugustRevisionEnabled={
        isAugustRevisionEnabled || alwaysUseAugustRevisionDfBStyles
      }
    />
  );

  const inlineCreateSection = isEditing &&
    currentAction === SectionsActionType.CREATE_SECTION && (
      <InlineCreateSection
        setIsEditing={setIsEditing}
        setCurrentAction={setCurrentAction}
        onCreateSection={onCreateSection}
        isAugustRevisionEnabled={!!isAugustRevisionEnabled}
      />
    );

  if (isAugustRevisionEnabled) {
    return (
      <Card.Module
        breakout="standard"
        withHeader={
          <Card.Header
            title={
              <Text className={styles.sectionsTitle} variant="label" isBold>
                {i18n.t("sections")}
              </Text>
            }
            actions={createButton}
            withMargin={false}
          />
        }
      >
        {listItems.length === 0 && !inlineCreateSection && (
          <Text className={styles.emptySectionsStateText} variant="paragraph">
            {i18n.t("sections_empty_cta")}
          </Text>
        )}
        <NavigationList
          listItems={listItems}
          hasWritePermissions={!!hasWritePermissions}
          onDrop={onDrop}
          inlineCreateSection={inlineCreateSection}
        />
      </Card.Module>
    );
  } else {
    return (
      <TwoColumnGridCard
        settingId={`stack_sections:${stack?.namespace_id}`}
        showDividerLine={false}
        cardTypeProps={{
          cardType: CardHeaderType.STACKED_SUBTITLE,
          title: i18n.t("sections"),
          featureLine: "stacks",
          dashCardType: "section_navigator",
        }}
        subtitle={hasWritePermissions ? i18n.t("sections_subtitle") : undefined}
        theme={
          getTheme(
            stack?.stack_data?.color_index,
            stack?.stack_data?.custom_color,
          ).subtle
        }
        isAlwaysCollapsed={listItems.length > 0}
        collapseBtnSize="large"
      >
        <NavigationList
          listItems={listItems}
          hasWritePermissions={!!hasWritePermissions}
          onDrop={onDrop}
          inlineCreateSection={inlineCreateSection}
          createButton={!isEditing ? createButton : undefined}
        />
      </TwoColumnGridCard>
    );
  }
};
